import { I18n } from '@calendly/i18n';
import { Avatar } from '@calendly/ui/components/avatar';
import { BareButton } from '@calendly/ui/components/bare-button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@calendly/ui/components/dropdown-menu';
import { ArrowChevronDownMiniIcon, ArrowChevronUpMiniIcon } from '@calendly/ui/components/icons';
import { Tooltip } from '@calendly/ui/components/tooltip';
import { LabelText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { colorTextStandard } from '@calendly/ui/theme';
import { useCallback, useState, type FC, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { navigationAnalyticsIdForProfileDropdown, trackProfileDropdownNavigation, useTrack } from 'hooks/analytics_events';
import { PROFILE_MENU_POPOVER_OFFSET_PX, profileDropdownMenuSurfaceCss } from './profileMenuDropdownChrome';
import type { MainNavAvatarRowProfile } from './types';

/** Shell chunk for the collapsed rail avatar control; dropdown content is portaled by CUI `DropdownMenu` popover. */
const shellCss = "s1tswhii";
const tileClipCss = "tuoteie";
const triggerCss = "t1awavep";
const avatarBlockCss = "abs5919";
const nameRowCss = "n1g0nm5r";
const nameCellCss = "nq8i8ql";
const chevronCss = "cqpzg39";

/** Optional profile fields: pass `profile` object and/or individual fields (merged with overrides). */
export type CollapsedAvatarRowProps = Partial<MainNavAvatarRowProfile> & {
  profile?: Partial<MainNavAvatarRowProfile>;
  /** Profile dropdown body; composed by the parent so this row stays free of billing/scheduling/Redux menu graph. */
  profileMenu: ReactNode;
};

/**
 * Account menu trigger for the **collapsed** CUI vertical rail header: avatar, truncated name, chevron, and a dropdown
 * whose body is passed as `profileMenu`.
 *
 * **Props:** Pass `profile` and/or the same fields spread at the top level (optional fragments of `MainNavAvatarRowProfile`).
 *
 * **Behavior:** Opens the profile menu on click; shows a name tooltip in this compact rail layout.
 */
export const CollapsedAvatarRow: FC<CollapsedAvatarRowProps> = props => {
  const {
    profile,
    profileMenu,
    ...overrides
  } = props;
  const isPhone = useDeviceType('isPhone');
  const {
    fullName = '',
    businessName = '',
    avatarSrc,
    avatarLetter,
    menuAccessLabel
  } = {
    ...profile,
    ...overrides
  };

  /** Profile menu open state; drives dropdown visibility and chevron direction. */
  const [menuOpen, setMenuOpen] = useState(false);
  const track = useTrack();
  const {
    pathname
  } = useLocation();
  const toggleProfileMenu = useCallback(() => {
    setMenuOpen(open => {
      if (!open) {
        trackProfileDropdownNavigation(track, 'profile_dropdown', pathname);
      }
      return !open;
    });
  }, [pathname, track]);

  /** This header only appears on the collapsed rail, where horizontal space for the name is tight. */
  const isMinimizedNav = true;

  /** Single visible character in the avatar fallback when there is no photo. */
  const resolvedLetter = avatarLetter ?? (fullName.trim().charAt(0) || undefined);

  /** Announces the control to assistive tech; prefers an explicit menu label when the product provides one. */
  const ariaLabel = menuAccessLabel ?? I18n.t('publisher.app_shell.avatar_row.account_menu_aria_label', {
    full_name: fullName,
    business_name: businessName
  });

  /** Hover tooltip with the full name when the rail layout truncates it in the button. */
  const showCaptionTooltip = isMinimizedNav && Boolean(fullName.trim());

  /** Optional `title` on the trigger when the tooltip path is off but we still want a quick identity hint on hover. */
  const fallbackTitle = !showCaptionTooltip && (fullName || businessName) ? I18n.t('publisher.app_shell.avatar_row.compact_identity_title', {
    full_name: fullName,
    business_name: businessName
  }) : undefined;
  return <div className={shellCss}>
      <DropdownMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)}>
        <Tooltip text={showCaptionTooltip ? fullName : null} placement="right">
          <div className={tileClipCss}>
            <DropdownMenuTrigger>
              <BareButton type="button" className={triggerCss} onClick={toggleProfileMenu} aria-haspopup="menu" aria-expanded={menuOpen} aria-label={ariaLabel} title={fallbackTitle} data-analytics-id={navigationAnalyticsIdForProfileDropdown('profile_dropdown')}>
                <span className={avatarBlockCss} aria-hidden>
                  <Avatar size="small" src={avatarSrc} letter={resolvedLetter} />
                </span>
                <div className={nameRowCss} aria-hidden>
                  <div className={nameCellCss}>
                    <LabelText size="small" color={colorTextStandard}>
                      {fullName}
                    </LabelText>
                  </div>
                  <span className={chevronCss}>
                    {menuOpen ? <ArrowChevronUpMiniIcon title="" /> : <ArrowChevronDownMiniIcon title="" />}
                  </span>
                </div>
              </BareButton>
            </DropdownMenuTrigger>
          </div>
        </Tooltip>
        {/*
         * Phone: omit fixed surface width, `placement`, and `offset` so CUI’s mobile bottom-sheet menu controls layout.
         * Desktop: keep narrow popover surface + anchored placement under the rail trigger.
         */}
        <DropdownMenuContent className={isPhone ? undefined : profileDropdownMenuSurfaceCss} {...isPhone ? {} : {
        placement: 'bottom start' as const,
        offset: PROFILE_MENU_POPOVER_OFFSET_PX
      }}>
          {profileMenu}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>;
};

require("./CollapsedAvatarRow.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./CollapsedAvatarRow.tsx");